import {
  type DismissMethod,
  type PopupComponentType,
  trackPopupDismissed,
  trackPopupDisplayed,
  trackPopupSuppressed,
} from "@/lib/analytics/segment";
import { claimSuppressionReport, getPromoSuppression, markPromoShown } from "@/lib/emailPromoSuppression";
import type { PromoModal } from "@/lib/promoModal/types";
import type { TriggerLabel } from "@/types/contentful/emailPromoModalTrigger";

const COMPONENT_TYPE: PopupComponentType = "emailPromoModal";

interface ShowModalArgs {
  modal: PromoModal;
  triggerType: TriggerLabel;
  isPreview: boolean;
  setShownAt: (shownAt: number) => void;
  onShow?: () => void;
}

/**
 * Every trigger routes through here, so suppression is always checked at fire time and the
 * session-marking rule can't drift between paths. Module scope — not a component-local
 * function or a hook return — keeps it out of the delay effect's dependencies.
 */
export function showModal({ modal, triggerType, isPreview, setShownAt, onShow }: ShowModalArgs) {
  const popupId = modal.id;
  // Untargeted campaigns also honor the legacy global suppression keys.
  const suppressionReason = getPromoSuppression({ id: popupId, isGlobal: !modal.pagePaths?.length }, isPreview);

  if (suppressionReason) {
    if (claimSuppressionReport()) trackPopupSuppressed({ popupId, componentType: COMPONENT_TYPE, suppressionReason });
    return;
  }

  markPromoShown(popupId);
  setShownAt(Date.now());
  trackPopupDisplayed({ popupId, componentType: COMPONENT_TYPE, triggerType });
  onShow?.();
}

interface DismissalArgs {
  popupId: string;
  shownAt: number | null;
  dismissMethod: DismissMethod;
}

/**
 * The only event the popup reports for itself after opening. Its form and CTA are shared
 * components that carry `popupId` on their own events, and a CTA click is a conversion rather
 * than an abandonment, so it is deliberately not a dismissal.
 */
export function trackDismissal({ popupId, shownAt, dismissMethod }: DismissalArgs) {
  const timeVisibleMs = shownAt === null ? 0 : Date.now() - shownAt;

  trackPopupDismissed({ popupId, componentType: COMPONENT_TYPE, timeVisibleMs, dismissMethod });
}
